import type { CreateExpenseInput, ExpenseCategory, ExpenseDetail, ExpensePaymentMethod, ExpenseTaxMode } from '../types/expenses';
import { EXPENSE_CATEGORIES_WITH_SUBCATEGORY } from './expenseActions';
import { calculateExpenseTaxBreakdown } from './expenseTax';

// Registrar gasto como asistente (FI-22, Entrega 1): lógica PURA — pasos, qué falta en cada uno, y el
// ida y vuelta entre el gasto guardado y el borrador del asistente. Usa la MISMA API de gastos de hoy
// (createExpense / updateExpense), no el camino del ticket: así conserva el desglose de IVA, el
// empleado en Sueldos, los borradores y la edición. Sin React, para poder probarla.

export type ExpenseStep = 'STORE' | 'CATEGORY' | 'WHAT' | 'AMOUNT' | 'WHEN' | 'REVIEW';

/** Elección explícita de «Gasto general (sin tienda)» — distinta de «todavía no elige». */
export const GENERAL_STORE = 'GENERAL';

export interface ExpenseDraft {
  /** '' = sin elegir · GENERAL_STORE · id de tienda. */
  storeChoice: string;
  category: ExpenseCategory | '';
  subcategory: string;
  concept: string;
  /** Proveedor libre, o el nombre del empleado cuando la categoría es Sueldos. */
  vendorName: string;
  amount: string;
  taxMode: ExpenseTaxMode | '';
  taxPercentage: string;
  dateIso: string;
  paymentMethod: ExpensePaymentMethod | '';
  invoiced: '' | 'yes' | 'no';
  reference: string;
  notes: string;
}

const positive = (text: string): number | null => { const n = Number(text); return Number.isFinite(n) && n > 0 ? n : null; };

export function hasSubcategory(category: ExpenseCategory | ''): boolean {
  return category !== '' && EXPENSE_CATEGORIES_WITH_SUBCATEGORY.includes(category);
}

/** ¿Se pregunta la tienda? Solo a dueño/gerente con más de una; el empleado tiene la suya forzada. */
export function asksStore(storeCount: number, isEmployee: boolean): boolean {
  return !isEmployee && storeCount > 1;
}

/**
 * Borrador vacío: nada preseleccionado, salvo la tienda cuando no es una elección (única, o la del
 * empleado). Un dueño sin tiendas cae en «Gasto general».
 */
export function emptyExpenseDraft(options: { storeIds: string[]; isEmployee: boolean }): ExpenseDraft {
  const { storeIds, isEmployee } = options;
  const storeChoice = asksStore(storeIds.length, isEmployee) ? '' : (storeIds[0] ?? GENERAL_STORE);
  return { storeChoice, category: '', subcategory: '', concept: '', vendorName: '', amount: '', taxMode: '', taxPercentage: '', dateIso: '', paymentMethod: '', invoiced: '', reference: '', notes: '' };
}

/** Un gasto guardado, listo para editarse en el asistente (abre en Revisar con todo encogido). */
export function expenseDraftFromExpense(expense: ExpenseDetail): ExpenseDraft {
  return {
    storeChoice: expense.storeId ?? GENERAL_STORE,
    category: expense.category,
    subcategory: expense.subcategory ?? '',
    concept: expense.concept,
    vendorName: expense.vendorName ?? '',
    amount: expense.amount,
    taxMode: expense.taxMode,
    taxPercentage: expense.taxMode === 'CUSTOM' ? (expense.taxPercentage ?? '') : '',
    dateIso: expense.expenseDate,
    paymentMethod: expense.paymentMethod ?? '',
    invoiced: expense.isInvoiced ? 'yes' : 'no',
    reference: expense.reference ?? '',
    notes: expense.notes ?? '',
  };
}

export function expenseSteps(askStore: boolean): ExpenseStep[] {
  const head: ExpenseStep[] = askStore ? ['STORE'] : [];
  return head.concat(['CATEGORY', 'WHAT', 'AMOUNT', 'WHEN', 'REVIEW']);
}

export function nextExpenseStep(step: ExpenseStep, steps: ExpenseStep[]): ExpenseStep {
  const index = steps.indexOf(step);
  return steps[index + 1] ?? step;
}

export function previousExpenseStep(step: ExpenseStep, steps: ExpenseStep[]): ExpenseStep {
  const index = steps.indexOf(step);
  return index > 0 ? steps[index - 1]! : step;
}

/** Por qué no se puede avanzar en este paso (null = se puede). El pie lo muestra tal cual. */
export function expenseStepBlocker(step: ExpenseStep, draft: ExpenseDraft, ctx: { dateReady: boolean; steps: ExpenseStep[] }): string | null {
  switch (step) {
    case 'STORE': return draft.storeChoice ? null : 'Elige la tienda, o «Gasto general».';
    case 'CATEGORY':
      if (!draft.category) return 'Elige el tipo de gasto.';
      if (hasSubcategory(draft.category) && !draft.subcategory.trim()) return 'Elige la subcategoría.';
      return null;
    case 'WHAT':
      if (!draft.concept.trim()) return 'Escribe qué pagaste.';
      if (draft.category === 'PAYROLL' && !draft.vendorName.trim()) return 'Elige el empleado.';
      return null;
    case 'AMOUNT':
      if (!positive(draft.amount)) return 'Escribe cuánto pagaste.';
      if (!draft.taxMode) return 'Elige cómo va el IVA.';
      if (draft.taxMode === 'CUSTOM' && draft.taxPercentage.trim() === '') return 'Escribe el porcentaje de IVA.';
      return null;
    case 'WHEN':
      if (!ctx.dateReady || !draft.dateIso) return 'Elige la fecha y la hora.';
      if (!draft.paymentMethod) return 'Elige cómo pagaste.';
      if (!draft.invoiced) return 'Di si te dieron factura.';
      return null;
    case 'REVIEW': {
      // Revisar solo se completa si todo lo anterior está completo (p. ej. un gasto editado al que
      // le falta algo que antes no era obligatorio).
      for (const previous of ctx.steps) {
        if (previous === 'REVIEW') break;
        const reason = expenseStepBlocker(previous, draft, ctx);
        if (reason) return reason;
      }
      return null;
    }
    default: return null;
  }
}

/** Total real del gasto según el IVA elegido (lo que se paga), o null si aún no se puede calcular. */
export function expenseDraftTotal(draft: ExpenseDraft): number | null {
  const amount = positive(draft.amount);
  if (!amount || !draft.taxMode) return null;
  return calculateExpenseTaxBreakdown(amount, draft.taxMode, draft.taxPercentage === '' ? null : Number(draft.taxPercentage)).totalAmount;
}

/** El mismo payload que enviaba el formulario anterior (sin `confirm`, que decide el botón). */
export function buildExpensePayload(draft: ExpenseDraft): CreateExpenseInput {
  if (!draft.category || !draft.taxMode || !draft.paymentMethod) throw new Error('El gasto está incompleto.');
  return {
    storeId: draft.storeChoice && draft.storeChoice !== GENERAL_STORE ? draft.storeChoice : null,
    expenseDate: draft.dateIso,
    category: draft.category,
    subcategory: hasSubcategory(draft.category) ? (draft.subcategory.trim() || null) : null,
    concept: draft.concept.trim(),
    amount: Number(draft.amount),
    taxMode: draft.taxMode,
    taxPercentage: draft.taxMode === 'CUSTOM' ? Number(draft.taxPercentage) : null,
    isInvoiced: draft.invoiced === 'yes',
    paymentMethod: draft.paymentMethod,
    vendorName: draft.vendorName.trim() || null,
    reference: draft.reference.trim() || null,
    notes: draft.notes.trim() || null,
  };
}
